import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiClient, createApiClient, type CapabilityMap } from '../lib/api-client';
import { clearStoredToken, getStoredToken, setStoredToken } from '../lib/token-storage';

interface User {
  id: string;
  email: string;
  tenant_id: string;
  role: string;
  role_id?: string | null;
  /** Capability flags from the user's role. Gate admin UI on these. */
  capabilities: CapabilityMap;
}

interface AuthContextValue {
  user: User | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  token: string | null;
  /**
   * Absolute API base URL (without trailing `/api/...`). `null` when using
   * same-origin requests through a proxy (SPA mode). Populated in embed
   * mode so hooks that bypass ApiClient (e.g. XHR-based file uploads)
   * can still reach the server.
   */
  apiBaseUrl: string | null;
  externalEmail: string | null;
  configError: string | null;
  login: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => void;
  apiClient: ApiClient;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useApiClient(): ApiClient {
  return useAuth().apiClient;
}

interface AuthProviderProps {
  children: ReactNode;
  externalToken?: string | null | undefined;
  externalEmail?: string | null | undefined;
  apiBaseUrl?: string | null | undefined;
}

export function AuthProvider({ children, externalToken, externalEmail, apiBaseUrl }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  const configError =
    externalToken && externalEmail
      ? 'Configuration error: both "email" and "token" attributes are set. Use one or the other.'
      : null;

  const apiClient = useMemo(
    () =>
      createApiClient({
        getToken: () => tokenRef.current,
        onUnauthorized: () => {
          clearStoredToken();
          tokenRef.current = null;
          setUser(null);
          setStatus('idle');
        },
        baseUrl: apiBaseUrl ?? undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (configError) return;

    const initialToken = externalToken ?? getStoredToken();
    if (!initialToken) {
      setStatus('idle');
      return;
    }

    tokenRef.current = initialToken;
    setStatus('loading');

    apiClient
      .getMe()
      .then((u) => {
        if (externalToken) {
          setStoredToken(externalToken);
        }
        setUser({
          id: u.id,
          email: u.email,
          tenant_id: u.tenant_id,
          role: u.role,
          role_id: u.role_id ?? null,
          capabilities: u.capabilities ?? {},
        });
        setStatus('success');
      })
      .catch(() => {
        clearStoredToken();
        tokenRef.current = null;
        setStatus('idle');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string): Promise<void> => {
    setError(null);
    try {
      await apiClient.requestCode(email);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send code';
      setError(msg);
      throw err;
    }
  };

  const verifyCode = async (email: string, code: string): Promise<void> => {
    setError(null);
    setStatus('loading');
    try {
      const result = await apiClient.verifyCode(email, code);
      setStoredToken(result.token);
      tokenRef.current = result.token;
      // verify_code doesn't carry capabilities; fetch /auth/me for the full
      // principal (role_id + capabilities) so admin UI gating is correct.
      const me = await apiClient.getMe().catch(() => null);
      setUser({
        id: result.user.id,
        email: result.user.email,
        tenant_id: result.user.tenant_id,
        role: me?.role ?? result.user.role,
        role_id: me?.role_id ?? null,
        capabilities: me?.capabilities ?? {},
      });
      setStatus('success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      setError(msg);
      setStatus('error');
      throw err;
    }
  };

  const logout = (): void => {
    clearStoredToken();
    tokenRef.current = null;
    setUser(null);
    setStatus('idle');
  };

  const token = tokenRef.current;
  const resolvedEmail = externalEmail ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        error,
        token,
        apiBaseUrl: apiBaseUrl ?? null,
        externalEmail: resolvedEmail,
        configError,
        login,
        verifyCode,
        logout,
        apiClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
